import React from "react";
import { Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FilePickerDialog from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";
import ThumbnailDisplay from "@/modules/hub/features/FileManagement/components/ThumbnailDisplay/ThumbnailDisplay";
import { landingPageKeys } from "@/modules/hub/features/ProductCatalog/landingPages";
import { AdminCatalogProduct } from "../../api/fetchCatalogProducts";
import { SaveCatalogProductRequest } from "../../api/createCatalogProduct";
import { useCatalogProductForm } from "../../hooks/useCatalogProductForm";
import { CATALOG_AUDIENCES, CATALOG_TRADES } from "../../config/catalogOptions";
import ToggleChipGroup from "../ToggleChipGroup/ToggleChipGroup";

const NO_LANDING_PAGE = "__none__";

interface CatalogProductFormProps {
  initialData?: AdminCatalogProduct | null;
  onSubmit: (data: SaveCatalogProductRequest) => Promise<void>;
  isLoading: boolean;
}

const CatalogProductForm: React.FC<CatalogProductFormProps> = ({
  initialData,
  onSubmit,
  isLoading,
}) => {
  const {
    form,
    isEditing,
    categories,
    stages,
    loadingOptions,
    thumbnailDisplayUrl,
    thumbnailFileName,
    isThumbnailPickerOpen,
    openThumbnailPicker,
    closeThumbnailPicker,
    handleThumbnailSelected,
    handleRemoveThumbnail,
    submit,
  } = useCatalogProductForm({ initialData, onSubmit });
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = form;

  const featured = watch("featured");

  return (
    <form className="space-y-6" onSubmit={submit}>
      <div className="space-y-4">
        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="name"
          >
            Name *
          </label>
          <Input
            className="dark:bg-gray-800"
            id="name"
            placeholder="e.g., Field Coach"
            {...register("name")}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="slug"
          >
            Slug *
          </label>
          <Input
            className="dark:bg-gray-800"
            id="slug"
            placeholder="e.g., field-coach"
            {...register("slug")}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Used in the catalog URL: /catalog/&#123;slug&#125;. Lowercase
            letters, numbers, and hyphens.
          </p>
          {errors.slug && (
            <p className="text-red-500 text-sm mt-1">{errors.slug.message}</p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="tagline"
          >
            Tagline *
          </label>
          <Textarea
            className="dark:bg-gray-800"
            id="tagline"
            placeholder="One-line description shown on the card and hero"
            rows={2}
            {...register("tagline")}
          />
          {errors.tagline && (
            <p className="text-red-500 text-sm mt-1">
              {errors.tagline.message}
            </p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="badge"
          >
            Badge
          </label>
          <Input
            className="dark:bg-gray-800"
            id="badge"
            placeholder='e.g., "Most Popular", "AI · New" (optional)'
            {...register("badge")}
          />
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="categoryId"
          >
            Category *
          </label>
          <Controller
            control={control}
            name="categoryId"
            render={({ field }) =>
              // Radix Select must not mount before its items exist: its hidden
              // native form <select> can't represent the value without a
              // matching option, snaps to "", and fires onValueChange("") —
              // silently wiping the saved category on the edit page.
              loadingOptions ? (
                <div className="flex h-9 w-full items-center rounded-md border border-input bg-white px-3 py-1 text-sm text-gray-500 shadow-sm dark:bg-gray-800 dark:border-slate-700 dark:text-gray-400">
                  Loading categories...
                </div>
              ) : (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="dark:bg-gray-800">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            }
          />
          {errors.categoryId && (
            <p className="text-red-500 text-sm mt-1">
              {errors.categoryId.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
            Built for
          </label>
          <Controller
            control={control}
            name="audiences"
            render={({ field }) => (
              <ToggleChipGroup
                onChange={field.onChange}
                options={CATALOG_AUDIENCES.map((audience) => ({
                  value: audience,
                  label: audience,
                }))}
                selected={field.value}
              />
            )}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
            Trades
          </label>
          <Controller
            control={control}
            name="trades"
            render={({ field }) => (
              <ToggleChipGroup
                onChange={field.onChange}
                options={CATALOG_TRADES.map((trade) => ({
                  value: trade,
                  label: trade,
                }))}
                selected={field.value}
              />
            )}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
            Available to stages
          </label>
          <Controller
            control={control}
            name="stageUuids"
            render={({ field }) => (
              <ToggleChipGroup
                onChange={field.onChange}
                options={stages.map((stage) => ({
                  value: stage.uuid,
                  label: stage.name,
                }))}
                selected={field.value}
              />
            )}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Members only see entries available to their stage. Leave all
            unselected to show the entry to every stage.
          </p>
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="landingPageKey"
          >
            Landing page
          </label>
          <Controller
            control={control}
            name="landingPageKey"
            render={({ field }) => (
              <Select
                onValueChange={(value) =>
                  field.onChange(value === NO_LANDING_PAGE ? null : value)
                }
                value={field.value ?? NO_LANDING_PAGE}
              >
                <SelectTrigger className="dark:bg-gray-800">
                  <SelectValue placeholder="No landing page" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_LANDING_PAGE}>
                    No landing page
                  </SelectItem>
                  {landingPageKeys.map((key) => (
                    <SelectItem key={key} value={key}>
                      {key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Landing pages ship with the app. Entries without one show a
            non-clickable card until a page is built.
          </p>
        </div>

        <div className="[&>div]:max-w-none [&>div>button]:dark:bg-gray-800">
          <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
            Thumbnail
          </label>
          <ThumbnailDisplay
            onRemove={handleRemoveThumbnail}
            onSelect={openThumbnailPicker}
            thumbnailFileName={thumbnailFileName}
            thumbnailUrl={thumbnailDisplayUrl || null}
          />
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="sortOrder"
          >
            Sort order
          </label>
          <Input
            className="dark:bg-gray-800 max-w-[8rem]"
            id="sortOrder"
            min={0}
            type="number"
            {...register("sortOrder")}
          />
          {errors.sortOrder && (
            <p className="text-red-500 text-sm mt-1">
              {errors.sortOrder.message}
            </p>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label
                className="block text-sm font-medium text-gray-900 dark:text-white"
                htmlFor="featured"
              >
                Featured
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Featured entries appear in the hero carousel on the catalog
                page.
              </p>
            </div>
            <Controller
              control={control}
              name="featured"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  id="featured"
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
          {featured && (
            <div>
              <label
                className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
                htmlFor="featuredOrder"
              >
                Hero slide order
              </label>
              <Input
                className="dark:bg-gray-800 max-w-[8rem]"
                id="featuredOrder"
                min={0}
                type="number"
                {...register("featuredOrder")}
              />
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <label
                className="block text-sm font-medium text-gray-900 dark:text-white"
                htmlFor="isPublished"
              >
                Published
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                When published, the entry is visible in the member catalog.
              </p>
            </div>
            <Controller
              control={control}
              name="isPublished"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  id="isPublished"
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button
          disabled={isLoading}
          onClick={() => window.history.back()}
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button disabled={isLoading || loadingOptions} type="submit">
          {isLoading
            ? "Saving..."
            : isEditing
              ? "Save Changes"
              : "Create Entry"}
        </Button>
      </div>
      <FilePickerDialog
        allowedFileTypes={[
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
        ]}
        isOpen={isThumbnailPickerOpen}
        multiSelect={false}
        namespace="product_catalog"
        onClose={closeThumbnailPicker}
        onSelect={handleThumbnailSelected}
        title="Select Thumbnail"
      />
    </form>
  );
};

export default CatalogProductForm;
